import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { AccountServices as _account } from '@/services/store/account.services';
import { SelfServiceService } from '@/services/store';
import { Address, IdentityDetails, InsurancePolicy } from '@/interfaces';
import { HOME_PAGE } from '@/constants/urls.const';
import { ENTRA_SCOPES } from '@/constants/auth.const';
import { useMsal } from '@/composables/useMsal';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { JP_LOGGEDOUT } from '@/constants/jeopardy-codes.const';
import { revokeLoginUrl } from '@/constants/forms-api.const';

export const useAccountStore = defineStore('Account', () => {
  const loginVisible = ref(false);
  const identityDetails = ref<Partial<IdentityDetails>>();
  const prefillForm = ref(false);
  const partyMatched = ref(false);
  const authenticationChecked = ref(false);
  const authenticationResult = ref(false);
  const selfServiceMakePaymentUrl = ref<string>();
  const renewalPolicies = ref<InsurancePolicy[]>([]);
  const { instance: msalInstance, inProgress } = useMsal();

  function hasMsalSession() {
    return msalInstance.getAllAccounts().length > 0;
  }

  async function getAccessToken(): Promise<string> {
    const account = msalInstance.getAllAccounts()?.[0];
    if (!account) return '';
    try {
      const tokenResult = await msalInstance.acquireTokenSilent({
        scopes: [...ENTRA_SCOPES],
        account,
      });
      if (!tokenResult?.accessToken) throw new Error('No access token returned from acquireTokenSilent');
      return tokenResult.accessToken;
    } catch {
      return '';
    }
  }

  async function getLogoutHint(account: ReturnType<typeof msalInstance.getAllAccounts>[number] | undefined) {
    if (!account) return undefined;

    try {
      const tokenResult = await msalInstance.acquireTokenSilent({
        scopes: [...ENTRA_SCOPES],
        account,
      });
      const tokenClaims = tokenResult.idTokenClaims as Record<string, unknown> | undefined;
      const logoutHint = tokenClaims?.login_hint;

      if (typeof logoutHint === 'string') return logoutHint;
    } catch {
      return account.username;
    }

    return account.username;
  }

  function setAuthenticationState(isAuthenticated: boolean) {
    authenticationResult.value = isAuthenticated;
    authenticationChecked.value = true;
  }

  const resetAuthCheckStatus = () => {
    authenticationChecked.value = false;
  };

  async function syncFromMsal() {
    const isAuthenticated = hasMsalSession();
    setAuthenticationState(isAuthenticated);
  }

  async function authenticateBackendSession(token: string) {
    const clientId = process.env.VUE_APP_ENTRA_CLIENT_ID;
    const response = await _account.authenticate(token, clientId);

    if (!response?.success) {
      throw new Error(response?.error || 'Unable to initialize backend session');
    }

    await syncFromMsal();
  }

  const isAuthenticated = computed(() =>
    authenticationChecked.value
      ? Promise.resolve(authenticationResult.value)
      : Promise.resolve(
          _account.isAuthenticated().then((result) => {
            authenticationResult.value = result;
            authenticationChecked.value = true;
            if (result) return Promise.resolve(fetchIdentityDetails().then(() => authenticationResult.value));
            return result;
          })
        )
  );

  async function handleAuthenticated(formUrl: string | undefined) {
    if (formUrl && (location.pathname + location.search).toLowerCase() !== formUrl.toLowerCase()) {
      //redirect back to form
      window.location.href = formUrl ?? '/';
    } else {
      await fetchIdentityDetails();
    }
  }

  async function handleUnauthenticated(formUrl: string | undefined) {
    await msalInstance.loginRedirect({
      scopes: [...ENTRA_SCOPES],
      state: formUrl,
      prompt: 'login',
    });
  }

  /**
   * Go to Entra login page with email prefill
   *
   * @param formUrl Target URL
   * @param hint Prefill Email
   */
  async function signInWithHint(formUrl: string, hint: string) {
    await msalInstance.loginRedirect({
      scopes: [...ENTRA_SCOPES],
      state: formUrl,
      loginHint: hint,
    });
  }

  /**
   * Get Identity Details
   */
  const fetchIdentityDetails = async (forceRefresh?: boolean) => {
    try {
      const res = await _account.fetchIdentityDetails(forceRefresh ?? false);
      if ((res as { errors?: Array<{ message: string }> })?.errors?.[0]?.message === JP_LOGGEDOUT) {
        throw new Error(JP_LOGGEDOUT);
      }
      identityDetails.value = res;
      prefillForm.value = true;
    } catch (e) {
      // revoke all login session before login
      // use case: member idle for 20 minutes
      await revokeSessions();
      throw new Error(JP_LOGGEDOUT);
    }
  };

  const fetchPolicyCurrent = async (policyNumber: string) => {
    return await SelfServiceService.getInsurancePolicyCurrent(policyNumber);
  };

  const fetchPolicyRenewal = async (policyNumber: string) => {
    let renewalPolicy = renewalPolicies?.value?.find((x) => x?.policyNumber === policyNumber.toUpperCase());

    if (!renewalPolicy) {
      renewalPolicy = await SelfServiceService.getInsurancePolicyRenewal(policyNumber);
      renewalPolicies.value.push(renewalPolicy as InsurancePolicy);
    }

    return renewalPolicy;
  };

  const fetchLoyaltyLevel = async (externalSystemId: string | null) => {
    const response = await _account.getLoyaltyType(externalSystemId);
    return response;
  };

  /**
   * Back to account overview
   */
  const cancelForm = async () => {
    try {
      const url = await _account.getSelfServiceAccountOverviewPage();
      window.location.href = url;
    } catch (error) {
      console.warn('Unable to retrieve account overview URL: ', error);
    }
  };

  /**
   * Check authenticated state and callback
   */
  const authenticateOrCallBack = async (callBackIfAuthenticated: () => void) => {
    if (!hasMsalSession()) {
      if (inProgress.value !== 'none') return;
      await msalInstance.loginRedirect({ scopes: [...ENTRA_SCOPES] });
    } else {
      setAuthenticationState(true);
      callBackIfAuthenticated();
    }
  };

  /**
   * Revoke active user sessions
   */
  const revokeSessions = async () => {
    try {
      await fetch(revokeLoginUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
      });
    } catch {
      // do nothing
    }
    // Clear cache to Stop silentAuth from re-authenticating when Graph fails.
    await msalInstance.clearCache();
  };

  /**
   * Clear session data and redirect to home page
   */
  const logout = async () => {
    setAuthenticationState(false);

    try {
      await _account.logOut();
      const account = msalInstance.getAllAccounts()?.[0];
      await msalInstance.logoutRedirect({
        logoutHint: await getLogoutHint(account),
        account,
      });
    } catch {
      globalThis.location.href = HOME_PAGE;
    }
  };

  /**
   * Silently re-acquire an MSAL token and re-establish the backend session..
   */
  async function silentMsalAuth(): Promise<void> {
    const account = msalInstance.getAllAccounts()[0];
    const tokenResult = await msalInstance.acquireTokenSilent({
      scopes: [...ENTRA_SCOPES],
      account,
      forceRefresh: true,
    });
    if (!tokenResult.idToken) throw new Error('No id token returned from acquireTokenSilent');
    await authenticateBackendSession(tokenResult.idToken);
  }

  /**
   * Attempt handleAuthenticated, falling back to silent re-auth if the backend session has expired.
   * Redirect to login if required
   */
  async function handleAuthenticatedWithFallback(formUrl: string | undefined): Promise<void> {
    try {
      await handleAuthenticated(formUrl);
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await handleUnauthenticated(formUrl);
        return;
      }

      // Backend session expired (401 / JP_LOGGEDOUT) - force a full login, do not attempt silent re-auth.
      if (e instanceof Error && e.message === JP_LOGGEDOUT) {
        await handleUnauthenticated(formUrl);
        return;
      }

      // Otherwise try silent re-auth then retry
      try {
        await silentMsalAuth();
        await handleAuthenticated(formUrl);
      } catch {
        await handleUnauthenticated(formUrl);
      }
    }
  }

  /**
   * Check Login Status
   */
  const authenticate = async (path: string, doNotRedirectBack: boolean = false, forceLogin = false) => {
    // Check for logout
    if (path.toLowerCase().includes('logout')) {
      logout();
      return;
    }
    // Validate authentication
    const formUrl = doNotRedirectBack ? undefined : path;

    if (!hasMsalSession()) {
      setAuthenticationState(false);
      if (forceLogin) await handleUnauthenticated(formUrl);
      return;
    }

    await handleAuthenticatedWithFallback(formUrl);
  };

  const togglePrefillDetails = () => {
    prefillForm.value = !prefillForm.value;
  };

  const setIdentityDetailsFirstName = (firstName: string) => {
    if (!identityDetails.value) identityDetails.value = {};
    identityDetails.value.firstName = firstName;
  };

  const getResidentialAddress = () => {
    if (identityDetails.value?.apttusId) return _account.getResidentialAddress(identityDetails.value.apttusId);
  };

  const getPostalAddress = () => {
    if (identityDetails.value?.apttusId) return _account.getPostalAddress(identityDetails.value.apttusId);
  };

  const getSelfServiceAccountOverviewUrl = () => {
    return _account.getSelfServiceAccountOverviewPage();
  };

  const getSelfServiceMakePaymentUrl = async () => {
    if (!selfServiceMakePaymentUrl.value) {
      selfServiceMakePaymentUrl.value = await _account.getSelfServiceMakePaymentUrl();
    }
    return selfServiceMakePaymentUrl?.value;
  };

  const getMembershipNumber = () => {
    return _account.getMembershipNumber();
  };

  const updateContactInformation = (
    d365Id: string,
    emailAddress: string,
    primaryPhoneNumber: string,
    additionalPhoneNumber: string
  ) => {
    return _account.updateContactInformation(d365Id, emailAddress, primaryPhoneNumber, additionalPhoneNumber);
  };

  const updateAddressInformation = (
    d365Id: string,
    residentialAddress?: Partial<Address>,
    postalAddress?: Partial<Address>
  ) => {
    return _account.updateAddressInformation(d365Id, residentialAddress, postalAddress);
  };

  const isUserNameTaken = (email: string) => {
    return _account.isUserNameTaken(email);
  };

  const searchOktaAccount = async (email: string) => {
    const res = await _account.searchOktaAccountByEmail(email);
    return res;
  };

  const matchIdentity = async (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    postcode: string,
    email: string
  ) => {
    const res = await _account.matchIdentity(firstName, lastName, dateOfBirth, postcode, email);
    return res;
  };

  const matchAndSetIdentity = async (
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    postcode: string,
    email: string,
    membershipCardNumber: string
  ) => {
    const res = await _account.matchAndSetIdentity(
      firstName,
      lastName,
      dateOfBirth,
      postcode,
      email,
      membershipCardNumber
    );
    return res;
  };

  const isProvisioned = async (email: string) => {
    const res = await _account.isProvisioned(email);
    return res;
  };

  const setPassword = async (password: string, isSelfServicePasswordChange: boolean = false) => {
    const res = await _account.setPassword(password, isSelfServicePasswordChange);
    return res;
  };

  const changePassword = (currentPassword: string, newPassword: string) => {
    return _account.changePassword(currentPassword, newPassword);
  };

  const signInWithCredentials = async (email: string) => {
    // ROPC flow is not supported by Entra ID. Redirecting to standard login.
    const accountOverviewUrl = await getSelfServiceAccountOverviewUrl();
    await msalInstance.loginRedirect({
      scopes: [...ENTRA_SCOPES],
      loginHint: email,
      redirectUri: accountOverviewUrl + '?pop=reg',
    });
  };

  return {
    loginVisible,
    identityDetails,
    prefillForm,
    partyMatched,
    isAuthenticated,
    authenticationResult,
    getAccessToken,
    syncFromMsal,
    authenticateBackendSession,
    fetchIdentityDetails,
    fetchLoyaltyLevel,
    cancelForm,
    authenticateOrCallBack,
    authenticate,
    togglePrefillDetails,
    setIdentityDetailsFirstName,
    getResidentialAddress,
    getPostalAddress,
    getSelfServiceAccountOverviewUrl,
    getSelfServiceMakePaymentUrl,
    getMembershipNumber,
    logout,
    updateContactInformation,
    updateAddressInformation,
    isUserNameTaken,
    searchOktaAccount,
    matchIdentity,
    matchAndSetIdentity,
    isProvisioned,
    setPassword,
    changePassword,
    signInWithHint,
    signInWithCredentials,
    fetchPolicyCurrent,
    fetchPolicyRenewal,
    handleUnauthenticated,
    resetAuthCheckStatus,
  };
});
